// useAddDatabaseModalState.js

import { useCallback, useMemo, useState } from 'react';

// Toast
import { toast } from 'react-toastify';

// Context
import { useDatabase } from 'context/DatabaseContext';
import { useNodeTypes } from 'context/NodeTypesContext';
import { useUser } from 'context/UserContext';

// Routing
import { useNavigate } from 'react-router-dom';

// Utilities
import { toLowerSnakeCase } from 'utilities/Strings';

// Services
import { updateNodeTypeMeta } from 'services/nodetypes/NodeTypeMutationService';

// Hooks
import { useCreateTypeForm } from 'components/addmodal/sections/createtype/useCreateTypeForm';
import { useDictionarySection } from 'components/addmodal/sections/dictionary/useDictionarySection';

export const STEP_LANDING = 'landing';
export const STEP_TEMPLATE = 'template';
export const STEP_CREATE = 'create';
export const STEP_FIELDS = 'fields';

export const CREATE_TAB_ABOUT = 'about';
export const CREATE_TAB_FIELDS = 'fields';

const DICTIONARY_TAB = 'template';

const buildDefaultDatabaseKey = (workspaceKeyValue, typeKeyValue) => (
    `${workspaceKeyValue}_${typeKeyValue}_all`.replace(/\//g, '_')
);

/**
 * useAddDatabaseModalState
 *
 * Manages wizard flow state, dictionary provisioning, and create-type editing for AddDatabaseModal.
 *
 * @param {object} params - Hook parameters.
 * @param {boolean} params.isOpen - Whether the modal is open.
 * @param {function} params.onClose - Called when the modal is dismissed.
 * @param {string|null} params.workspaceKey - Active workspace key.
 *
 * @returns {object} Grouped modal state and handlers for the AddDatabaseModal shell.
 */
export default function useAddDatabaseModalState({
    isOpen,
    onClose,
    workspaceKey,
}) {
    const { setActiveDatabase } = useDatabase();
    const { getAll } = useNodeTypes();
    const { user } = useUser();
    const navigate = useNavigate();

    const [step, setStep] = useState(STEP_LANDING);
    const [newTypeKey, setNewTypeKey] = useState('');
    const [isUpdatingType, setIsUpdatingType] = useState(false);

    const typeDefs = useMemo(() => getAll() || [], [getAll]);
    const userKey = user?.key || null;

    /**
     * handleClose
     *
     * Resets the modal wizard state and notifies the parent that the modal should close.
     */
    const handleClose = useCallback(() => {
        setStep(STEP_LANDING);
        setNewTypeKey('');
        setIsUpdatingType(false);
        onClose?.();
    }, [onClose]);

    /**
     * handleProvisionedTypeCreated
     *
     * Activates a newly provisioned template database and navigates to it.
     *
     * @param {string} typeKey - Newly created node type key.
     * @param {object} createdType - Created type metadata returned from provisioning.
     */
    const handleProvisionedTypeCreated = useCallback((typeKey, createdType) => {
        const resolvedTypeKey = String(typeKey || '').trim();
        if (!resolvedTypeKey || !workspaceKey) {
            handleClose();
            return;
        }

        const databaseKey = buildDefaultDatabaseKey(workspaceKey, resolvedTypeKey);
        const databaseTitle = String(createdType?.labelPlural || '').trim() || 'Items';

        setActiveDatabase?.({
            key: databaseKey,
            workspaceKey,
            type: resolvedTypeKey,
            title: databaseTitle,
            userKey,
            filters: [],
        });

        handleClose();
        navigate(`/database/${databaseKey}`);
    }, [handleClose, navigate, setActiveDatabase, userKey, workspaceKey]);

    /**
     * handleTypeCreated
     *
     * Stores the newly created type key and advances the wizard into the fields step.
     *
     * @param {string} typeKey - New type key created by the create-type flow.
     */
    const handleTypeCreated = useCallback((typeKey) => {
        setNewTypeKey(typeKey);
        setStep(STEP_FIELDS);
    }, []);

    const {
        query: dictionaryQuery,
        setQuery: setDictionaryQuery,
        loading: dictionaryLoading,
        error: dictionaryError,
        terms: dictionaryTerms,
        selectedTerm: dictionarySelectedTerm,
        selectedVariant: dictionarySelectedVariant,
        isVariantStep: dictionaryIsVariantStep,
        isVariantPreviewStep: dictionaryIsVariantPreviewStep,
        isProvisioning: dictionaryIsProvisioning,
        provisioningVariantKey,
        selectedFieldKeys: dictionarySelectedFieldKeys,
        workspaceKey: dictionaryWorkspaceKey,
        handleSelectTerm,
        handleSelectVariant,
        handleBackToTerms,
        handleBackToVariants,
        handleToggleSelectedField,
        handleUseSelectedVariant,
    } = useDictionarySection({
        isOpen,
        selectedTab: step,
        dictionaryTabValue: DICTIONARY_TAB,
        workspaceKey,
        availableTypes: typeDefs,
        onTypeSelect: handleProvisionedTypeCreated,
        onClose: () => {},
    });

    /**
     * handleTemplateBack
     *
     * Steps backward through the template provisioning flow or returns to the landing step.
     */
    const handleTemplateBack = useCallback(() => {
        if (dictionaryIsVariantPreviewStep) {
            handleBackToVariants();
            return;
        }

        if (dictionaryIsVariantStep) {
            handleBackToTerms();
            return;
        }

        setStep(STEP_LANDING);
    }, [dictionaryIsVariantPreviewStep, dictionaryIsVariantStep, handleBackToTerms, handleBackToVariants]);

    const isCreateFlow = step === STEP_CREATE || step === STEP_FIELDS;
    const hasCreatedType = Boolean(String(newTypeKey || '').trim());

    const {
        label,
        setLabel,
        labelPlural,
        description,
        setDescription,
        iconKey,
        setIconKey,
        defaultView,
        setDefaultView,
        isSavingType,
        canSaveType,
        labelHelperText,
        pluralHelperText,
        handleLabelPluralChange,
        handleCreateType,
    } = useCreateTypeForm({
        isOpen: isOpen && isCreateFlow,
        typeDefs,
        excludeTypeKey: newTypeKey,
        userKey,
        workspaceKey,
        onTypeSelect: handleTypeCreated,
        onClose: () => {},
    });

    const createTabs = useMemo(() => {
        const tabs = [
            {
                key: CREATE_TAB_ABOUT,
                label: 'About',
                onClick: () => setStep(STEP_CREATE),
            },
        ];

        if (hasCreatedType) {
            tabs.push({
                key: CREATE_TAB_FIELDS,
                label: 'Fields',
                onClick: () => setStep(STEP_FIELDS),
            });
        }

        return tabs;
    }, [hasCreatedType]);

    const selectedCreateTab = step === STEP_FIELDS ? CREATE_TAB_FIELDS : CREATE_TAB_ABOUT;

    const canSaveCreatedType = useMemo(() => {
        return hasCreatedType
            && Boolean(String(label || '').trim())
            && !labelHelperText
            && !pluralHelperText
            && !isSavingType
            && !isUpdatingType;
    }, [hasCreatedType, isSavingType, isUpdatingType, label, labelHelperText, pluralHelperText]);

    /**
     * handleSaveCreatedType
     *
     * Persists metadata updates for a newly created database type.
     *
     * @returns {Promise<void>} Resolves when the save flow completes.
     */
    const handleSaveCreatedType = useCallback(async () => {
        const cleanedTypeKey = String(newTypeKey || '').trim();
        const cleanedLabel = String(label || '').trim();
        const cleanedPlural = String(labelPlural || '').trim() || cleanedLabel;

        if (!cleanedTypeKey) return;
        if (!cleanedLabel) {
            toast.error('Database name is required.');
            return;
        }

        setIsUpdatingType(true);

        try {
            await updateNodeTypeMeta({
                workspaceKey: workspaceKey || null,
                typeKey: cleanedTypeKey,
                label: cleanedLabel,
                labelPlural: cleanedPlural,
                description,
                iconKey: toLowerSnakeCase(iconKey),
                defaultView,
            });
            toast.success('Database details updated.');
        } catch (error) {
            toast.error(error?.message || 'Could not update database details.');
        } finally {
            setIsUpdatingType(false);
        }
    }, [defaultView, description, iconKey, label, labelPlural, newTypeKey, workspaceKey]);

    const createdTypeLabel = useMemo(() => {
        const createdType = typeDefs.find((candidate) => String(candidate?.key || '').trim() === String(newTypeKey || '').trim());
        const sourceLabel = String(createdType?.label || label || '').trim();
        return sourceLabel ? sourceLabel : 'Item';
    }, [label, newTypeKey, typeDefs]);

    const createdTypeLabelLower = useMemo(() => {
        return createdTypeLabel.toLowerCase();
    }, [createdTypeLabel]);

    const modalTitle = hasCreatedType ? createdTypeLabel : 'Add Database';

    return {
        flowState: {
            isLandingStep: step === STEP_LANDING,
            isTemplateStep: step === STEP_TEMPLATE,
            isCreateStep: step === STEP_CREATE,
            isFieldsStep: step === STEP_FIELDS,
            isCreateFlow,
            hasCreatedType,
            modalTitle,
            createTabs,
            selectedCreateTab,
            createdTypeLabelLower,
        },
        dictionaryState: {
            query: dictionaryQuery,
            setQuery: setDictionaryQuery,
            loading: dictionaryLoading,
            error: dictionaryError,
            terms: dictionaryTerms,
            selectedTerm: dictionarySelectedTerm,
            selectedVariant: dictionarySelectedVariant,
            isVariantStep: dictionaryIsVariantStep,
            isVariantPreviewStep: dictionaryIsVariantPreviewStep,
            isProvisioning: dictionaryIsProvisioning,
            provisioningVariantKey,
            selectedFieldKeys: dictionarySelectedFieldKeys,
            workspaceKey: dictionaryWorkspaceKey,
            handleSelectTerm,
            handleSelectVariant,
            handleToggleSelectedField,
            handleUseSelectedVariant,
            handleTemplateBack,
        },
        createTypeState: {
            newTypeKey,
            label,
            setLabel,
            labelPlural,
            description,
            setDescription,
            iconKey,
            setIconKey,
            defaultView,
            setDefaultView,
            isSavingType,
            isUpdatingType,
            canSaveType,
            canSaveCreatedType,
            labelHelperText,
            pluralHelperText,
            handleLabelPluralChange,
            handleCreateType,
            handleSaveCreatedType,
        },
        modalActions: {
            handleClose,
            openCreateFlow: () => setStep(STEP_CREATE),
            openTemplateFlow: () => setStep(STEP_TEMPLATE),
            returnToLanding: () => setStep(STEP_LANDING),
        },
    };
}